import React, { createContext, FC, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const QuerySuspenderContext = createContext<{
  register: (id: string) => void;
  unregister: (id: string) => void;
  isSuspended: boolean;
}>({
  register: () => {},
  unregister: () => {},
  isSuspended: false
});

interface QuerySuspenderProviderProps {
  children: ReactElement;
}

export const QuerySuspenderProvider: FC<QuerySuspenderProviderProps> = ({ children }) => {
  const [suspenderIds, setSuspenderIds] = useState<{ [key: string]: boolean }>({});

  const register = useCallback(
    (id: string) => {
      if (!suspenderIds[id]) {
        setSuspenderIds(state => ({
          ...state,
          [id]: true
        }));
      }
    },
    [suspenderIds]
  );

  const unregister = useCallback(
    (id: string) =>
      setSuspenderIds(state => {
        const newState = { ...state };
        delete newState[id];

        return newState;
      }),
    []
  );
  const isSuspended = useMemo(() => !!Object.keys(suspenderIds).length, [suspenderIds]);

  const context = useMemo(
    () => ({
      register,
      unregister,
      isSuspended
    }),
    [register, unregister, isSuspended]
  );

  return <QuerySuspenderContext.Provider value={context}>{children}</QuerySuspenderContext.Provider>;
};

export const useQuerySuspender = (suspend: boolean) => {
  const uniqueId = useMemo(() => `suspend-${crypto.randomUUID()}`, []);
  const context = useContext(QuerySuspenderContext);

  useEffect(() => {
    if (suspend) {
      context?.register?.(uniqueId);
    }

    return () => context?.unregister?.(uniqueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suspend]);

  return context.isSuspended;
};
